import '@eg-maps/commons';
import '../node_modules/@eg-maps/base/public/index.bundle.esm.js';
import '@eg-maps/cluster-supercluster';
import { EGMap } from '@eg-maps/core';
import '@eg-maps/google-maps';
import '@eg-maps/mapbox';
import { mergeAndConcat } from 'merge-anything';
import * as React from 'react';
import { classNames } from 'uitk-react-utils';
import { useBrand } from '@shared-ui/brand-context';
import { EGDistances } from 'eg-distances';
import { defaultEGMapsConfig } from '../common/uitk-eg-map-config.esm.js';
import 'tslib';
import 'react-dom';
import 'uitk-react-icons';
import 'uitk-react-loader';
import 'uitk-react-layout-position';
import '@shared-ui/localization-context';
import '../types/uitk-eg-map-marker-type.esm.js';
import { UitkEGMapsType, UitkEGMapsContext } from '../types/uitk-eg-map-props.esm.js';

/* eslint-disable react-hooks/exhaustive-deps */
var defaultMapboxStyle = 'mapbox://styles/mapbox/streets-v11';
var UitkEGMapsContainer = function (_a) {
    var _b = _a.height, height = _b === void 0 ? '100%' : _b, _c = _a.width, width = _c === void 0 ? '100%' : _c, _d = _a.egDistancesEnabled, egDistancesEnabled = _d === void 0 ? false : _d, initialViewport = _a.initialViewport, accessToken = _a.accessToken, children = _a.children, mapType = _a.mapType, features = _a.features, onFeaturesPushed = _a.onFeaturesPushed, _e = _a.mapConfig, mapConfig = _e === void 0 ? {} : _e, className = _a.className, innerRef = _a.innerRef, beforeInit = _a.beforeInit, onInit = _a.onInit, clientPadding = _a.clientPadding;
    var mapProvider = mapType === UitkEGMapsType.GOOGLE ? 'google-maps' : 'mapbox';
    var _f = React.useState(undefined), config = _f[0], setConfig = _f[1];
    var _g = React.useState(), mapContext = _g[0], setMapContext = _g[1];
    var mapMemoValue = React.useMemo(function () { return ({ mapContext: mapContext, setMapContext: setMapContext }); }, [mapContext, setMapContext]);
    var mapRef = React.useRef();
    var brand = useBrand();
    var idSuffix = React.useMemo(function () { return Math.floor(Math.random() * 90); }, []);
    var gmapCfg = React.useMemo(function () {
        var _a, _b, _c, _d;
        return ({
            mapProvider: 'google-maps',
            googleMaps: {
                gmapsAddConfig: {
                    streetViewControl: true,
                    streetViewControlOptions: {
                        position: (_b = (_a = window === null || window === void 0 ? void 0 : window.google) === null || _a === void 0 ? void 0 : _a.maps) === null || _b === void 0 ? void 0 : _b.ControlPosition.RIGHT_TOP
                    },
                    zoomControlOptions: {
                        position: (_d = (_c = window === null || window === void 0 ? void 0 : window.google) === null || _c === void 0 ? void 0 : _c.maps) === null || _d === void 0 ? void 0 : _d.ControlPosition.RIGHT_TOP
                    },
                    scaleControl: true
                }
            }
        });
    }, []);
    var mapboxCfg = React.useMemo(function () { return ({
        mapProvider: 'mapbox',
        mapbox: {
            accessToken: accessToken,
            mapboxAddConf: {
                style: defaultMapboxStyle
            }
        }
    }); }, [accessToken]);
    var egDistancesConfig = React.useMemo(function () {
        var _a;
        if (egDistancesEnabled) {
            var egDistances = mapProvider === 'mapbox'
                ? new EGDistances({
                    provider: EGDistances.providers.mapbox,
                    providers: (_a = {},
                        _a[EGDistances.providers.mapbox] = {
                            // Why not using accessToken ?
                            key: 'pk.eyJ1IjoiZXhwZWRpYS1nZW8iLCJhIjoiY2s3NTc2aTExMDJwZDNmbnN1cDdvcmRzNSJ9.Grb_k2_kcl7NXEu6paSB1w'
                        },
                        _a)
                })
                : new EGDistances({ provider: EGDistances.providers.google });
            return { routes: { egDistances: egDistances } };
        }
        return {};
    }, [mapProvider, egDistancesEnabled]);
    React.useImperativeHandle(innerRef, function () { return ({
        map: function () { return mapContext === null || mapContext === void 0 ? void 0 : mapContext.mapObj; }
    }); });
    React.useEffect(function () {
        setConfig(undefined);
    }, [mapProvider, egDistancesConfig]);
    React.useEffect(function () {
        if (mapProvider && !config) {
            setConfig(mergeAndConcat(defaultEGMapsConfig(brand, function (cssPropertyName) {
                return getComputedStyle(mapRef.current).getPropertyValue(cssPropertyName);
            }), mapProvider === 'mapbox' ? mapboxCfg : gmapCfg, egDistancesConfig !== null && egDistancesConfig !== void 0 ? egDistancesConfig : {}, mapConfig || {}));
        }
    }, [mapProvider, brand, mapConfig, egDistancesConfig, setConfig]);
    React.useEffect(function () {
        if (config) {
            if (mapContext === null || mapContext === void 0 ? void 0 : mapContext.mapObj) {
                mapContext === null || mapContext === void 0 ? void 0 : mapContext.mapObj.unbindAll();
                setMapContext(undefined);
            }
            var mapObj_1 = new EGMap(config);
            beforeInit === null || beforeInit === void 0 ? void 0 : beforeInit(mapObj_1);
            mapObj_1.initMap(mapRef.current, true, initialViewport, clientPadding).then(function () {
                setMapContext({ mapObj: mapObj_1, mapConfig: config });
                onInit === null || onInit === void 0 ? void 0 : onInit(mapObj_1);
            });
            return function () {
                var _a;
                (_a = mapContext === null || mapContext === void 0 ? void 0 : mapContext.mapObj) === null || _a === void 0 ? void 0 : _a.unbindAll();
            };
        }
    }, [config]);
    React.useEffect(function () {
        if ((mapContext === null || mapContext === void 0 ? void 0 : mapContext.mapObj) && features) {
            var bounds = mapContext === null || mapContext === void 0 ? void 0 : mapContext.mapObj.pushFeatureData(features, 'replace');
            if (bounds)
                onFeaturesPushed === null || onFeaturesPushed === void 0 ? void 0 : onFeaturesPushed(mapContext === null || mapContext === void 0 ? void 0 : mapContext.mapObj, bounds);
        }
    }, [mapContext, features, onFeaturesPushed]);
    return (React.createElement("div", { className: classNames('uitk-eg-maps-container', 'uitk-eg-maps', className), style: { width: width, height: height }, id: "uitk-eg-maps-container-".concat(idSuffix), ref: mapRef }, (mapContext === null || mapContext === void 0 ? void 0 : mapContext.mapObj) && React.createElement(UitkEGMapsContext.Provider, { value: mapMemoValue }, children)));
};

export { UitkEGMapsContainer, defaultMapboxStyle };
